import { FinancialAccountNewCardTopUpRequest, OnboardingStep } from '@interfaces/models';
import { setOnboardingStep, getTopUpData, saveTopUp } from '@api/onboarding';
import { useCallback, FormEvent, useState, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTopUpForm } from '@pages/onboarding/hooks';
import { PayUTokenize } from '@shared/components/payu';
import { Routes } from '@shared/enums';

export const useOnboardingTopUp = () => {
  const { hasSubscriptionsAssigned } = useOutletContext<{ hasSubscriptionsAssigned: boolean }>();
  const payURef = useRef<PayUTokenize | null>(null);
  const navigate = useNavigate();

  const [wireTransferChecked, setWireTransferChecked] = useState(false);
  const { form } = useTopUpForm();

  const redirectStep: OnboardingStep = useMemo(
    () => (hasSubscriptionsAssigned ? OnboardingStep.COMPANY_POLICY : OnboardingStep.SUBSCRIPTIONS),
    [hasSubscriptionsAssigned],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding', 'top-up'],
    queryFn: () => getTopUpData(),
  });

  const onError = useCallback(() => {
    navigate(`../${Routes.TOP_UP_BALANCE_FAILED}`, { replace: true });
  }, [navigate]);

  const { mutate: saveTopUpMutation, isPending: isPendingTopUp } = useMutation({
    mutationFn: (data: FinancialAccountNewCardTopUpRequest) => saveTopUp(data),
    onSuccess: ({ redirectUri }) => {
      window.location.replace(redirectUri);
    },
    onError,
  });

  const { mutate: saveTopUpWireTransferMutation, isPending: isPendingWireTransfer } = useMutation({
    mutationFn: () => setOnboardingStep(redirectStep),
    onSuccess: () => navigate(`../${Routes[redirectStep as keyof typeof Routes]}`),
  });

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const card = await payURef.current?.tokenize('MULTI').catch(() => {
        return null;
      });

      // Trigger form validation. Card must be checked inside
      form.handleSubmit(() => {
        if (!card) return;
        saveTopUpMutation({ ...form.getValues(), card });
      })();
    },
    [form, saveTopUpMutation],
  );

  const onSubmitWireTransfer = useCallback(async () => {
    saveTopUpWireTransferMutation();
  }, [saveTopUpWireTransferMutation]);

  return {
    isPending: isPendingTopUp || isPendingWireTransfer,
    setWireTransferChecked,
    onSubmitWireTransfer,
    payUID: data?.payUID,
    wireTransferChecked,
    isLoading,
    onSubmit,
    payURef,
    form,
    data,
  };
};
